import { look, petSprite, studioPerson, T, type Seat } from "@bd/art";
import type { Company, Pet, Staff } from "@bd/sim";
import { Sprite } from "pixi.js";
import { texLazy } from "./textures";

export interface Point {
  x: number;
  y: number;
}

export interface Agent {
  id: string;
  /** visitors: shop and café customers, and testers in the lab */
  kind: "staff" | "pet" | "visitor";
  /** a visitor's room, the look they're drawn with, and when they head home (seconds) */
  visit?: { roomId: string; look: number; leaveAt: number; leaving: boolean };
  staff?: Staff;
  pet?: Pet;
  sprite: Sprite;
  pos: Point;
  path: Point[];
  seat?: Seat;
  sitting: boolean;
  timer: number;
  frame: number;
  frameT: number;
  facing: "front" | "back";
  sleeping?: boolean;
  /** not yet placed: goes straight to their desk instead of walking in */
  fresh?: boolean;
}

const ROLE_ROOMS: Record<string, string[]> = {
  designer: ["design"], developer: ["design", "playtest_lab"], artist: ["art_studio", "design"], writer: ["design"],
  sculptor: ["sculpt", "art_studio"], playtest_lead: ["playtest_lab", "design"], production: ["print_shop", "warehouse", "design"],
  marketer: ["marketing", "design"], community: ["shop_front", "marketing", "design"],
};

export interface Walkable {
  rooms: { id: string; type: string; x: number; y: number; w: number; h: number; door?: { x: number; y: number } }[];
  tilesW: number;
  tilesH: number;
  seats: Seat[];
}

function roomOf(w: Walkable, p: Point) {
  const tx = Math.floor(p.x / T);
  const ty = Math.floor(p.y / T);
  return w.rooms.find((r) => tx >= r.x && tx < r.x + r.w && ty >= r.y && ty < r.y + r.h);
}

function doorPoint(r: Walkable["rooms"][number], outside: boolean): Point {
  const d = r.door ?? { x: r.x + Math.floor(r.w / 2), y: r.y + r.h };
  // the door tile sits on the room edge; step half a tile in or out
  const inX = d.x >= r.x + r.w ? -0.6 : d.x < r.x ? 0.6 : 0;
  const inY = d.y >= r.y + r.h ? -0.6 : d.y < r.y ? 0.6 : 0;
  const k = outside ? -1 : 1;
  return { x: (d.x + 0.5 + inX * k) * T, y: (d.y + 0.5 + inY * k) * T };
}

/** Route via doors (rooms are walled except for their door). */
export function route(w: Walkable, from: Point, to: Point): Point[] {
  const a = roomOf(w, from);
  const b = roomOf(w, to);
  if (a && b && a.id === b.id) return [to];
  const pts: Point[] = [];
  if (a) {
    pts.push(doorPoint(a, false), doorPoint(a, true));
  }
  if (b) {
    const out = doorPoint(b, true);
    if (pts.length) pts.push({ x: out.x, y: pts[pts.length - 1].y }); // walk the corridor in an L
    pts.push(out, doorPoint(b, false));
  }
  pts.push(to);
  return pts;
}

export function staffSprite(s: Staff, facing: "front" | "back", sitting: boolean, frame: number, carry = false) {
  const lk = look(s.look);
  const pose = sitting ? "sit" : carry ? "carry" : frame ? "walk" : "stand";
  return texLazy(s.look * 97 + (facing === "front" ? 1 : 2) * 13 + (sitting ? 3 : 0) + frame * 7 + (carry ? 5 : 0) + 1_000_000, () => studioPerson(lk, { view: facing, pose, frame }));
}

export function petTexture(p: Pet, pose: "sit" | "sleep" | "walk", frame: number) {
  let h = 0;
  for (const ch of `${p.breed}|${pose}|${frame}`) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return texLazy(9_000_000 + (h % 1_000_000), () => petSprite(p.breed, pose, frame));
}

/** Pick a seat for a person: their role's room first, then any free seat. */
export function pickSeat(w: Walkable, s: Staff, taken: Set<string>): Seat | undefined {
  const pref = ROLE_ROOMS[s.role] ?? ["design"];
  for (const type of pref) {
    const seat = w.seats.find((st) => !taken.has(st.furnitureId) && w.rooms.find((r) => r.id === st.roomId)?.type === type);
    if (seat) return seat;
  }
  return w.seats.find((st) => !taken.has(st.furnitureId));
}

export function spawnPoint(w: Walkable, i: number): Point {
  // the corridor in front of the rooms, or the middle of the floor
  return { x: ((i * 3) % Math.max(4, w.tilesW - 2) + 1.5) * T, y: (w.tilesH - 1.5) * T };
}

export function randomFloorPoint(w: Walkable, r: () => number): Point {
  const room = w.rooms[Math.floor(r() * w.rooms.length)];
  if (!room) return { x: (w.tilesW / 2) * T, y: (w.tilesH / 2) * T };
  return { x: (room.x + 0.5 + r() * (room.w - 1)) * T, y: (room.y + 0.5 + r() * (room.h - 1)) * T };
}

export function companyAgentsKey(co: Company): string {
  return co.staff.map((s) => s.id).join(",") + "|" + co.pets.map((p) => p.id).join(",");
}
